import { useCallback, useEffect, useRef, useState } from "react";
import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent, triggerPageExit } from "@tm/insurance/analytics/xdm";
import { addDaysToDate, getPartnerOnClientSide, replaceHistory, replaceHistoryRemoveParams } from "@tm/insurance/utils";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { errorTrackEvent } from "../../shared/MwebAnalytics";
import { convertDateToUnix, convertUnixToDate, getIndiaTodayDate } from "../../../../utils";
import { navigateRecentSearchNextPage } from "../../../../utils/recentSearchNavigation";
import type { Country, Segment, PricingView, InsuranceCalendarConfig } from "../../CommonLandingMweb.types";
import { COMMON_LANDING_CONSTANTS } from "../../constants";
import {
  replaceHistoryPlanTypeAndSegment,
  replaceHistoryRemovePlanTypeAndSegment,
} from "./urlUtils";

/* ── Helper: map countries when switching between multitrip and single-trip ── */

interface CountryMappingResult {
  countries: Country[];
  urlCode: string | null;
}

function mapCountriesForTripOptionSwitch(params: {
  isGoingToMultitrip: boolean;
  isLeavingMultitrip: boolean;
  newCountryListValue: any[];
  urlCountryCodes: string[];
  savedCountryCodes: string;
}): CountryMappingResult | null {
  const {
    isGoingToMultitrip,
    isLeavingMultitrip,
    newCountryListValue,
    urlCountryCodes,
    savedCountryCodes,
  } = params;

  if (isGoingToMultitrip) {
    const regionCode =
      urlCountryCodes.includes("USA") ||
      urlCountryCodes.includes("CAN") ||
      urlCountryCodes.includes("WW")
        ? "WW"
        : "WWEUC";
    const regionCountry = newCountryListValue.find(
      (c: any) => c.code === regionCode
    );
    if (regionCountry) {
      return {
        countries: [{
          code: regionCountry.code,
          name: regionCountry.name ?? regionCountry.code,
          subtitle: regionCountry.subtitle,
        }],
        urlCode: regionCode,
      };
    }
    return { countries: [], urlCode: null };
  }

  if (isLeavingMultitrip) {
    const codesToRestore = savedCountryCodes
      ? savedCountryCodes.split(",").filter(Boolean)
      : urlCountryCodes;
    const restored: Country[] = newCountryListValue
      .filter((c: any) => c.code && codesToRestore.includes(c.code))
      .map((c: any) => ({
        code: c.code,
        name: c.name ?? c.code,
        subtitle: c.subtitle,
      }));
    if (restored.length) {
      return { countries: restored, urlCode: codesToRestore.join(",") };
    }
    return { countries: [], urlCode: null };
  }

  return null;
}

/* ── Helper: recalculate dates based on new trip option's calendar config ── */

interface DateRecalcResult {
  fromDate: Date;
  toDate: Date;
  changed: boolean;
}

function recalculateDatesForTripOption(params: {
  option: any;
  targetSegConfig: InsuranceCalendarConfig | undefined;
  isGoingToMultitrip: boolean;
  isLeavingMultitrip: boolean;
  previousCalendarType: string | null;
  currentFromDate: Date;
  currentToDate: Date;
}): DateRecalcResult {
  const {
    option,
    targetSegConfig,
    isGoingToMultitrip,
    isLeavingMultitrip,
    previousCalendarType,
    currentFromDate,
    currentToDate,
  } = params;

  const newConfig: InsuranceCalendarConfig | undefined = isGoingToMultitrip
    ? option?.config
    : targetSegConfig || option?.config;

  const isSingleCalendar =
    option?.calenderSelectionType === "SINGLE" && newConfig?.durationAllowed;
  const wasSingleCalendar =
    isLeavingMultitrip || previousCalendarType === "SINGLE";

  let newToDate = currentToDate;

  if (isSingleCalendar && newConfig?.durationAllowed) {
    newToDate =
      addDaysToDate(currentFromDate, newConfig.durationAllowed) || currentFromDate;
  } else if (wasSingleCalendar) {
    const minEndDays = newConfig?.insPurchaseEndDateMinLimitInDays ?? 7;
    newToDate = addDaysToDate(currentFromDate, minEndDays) || currentFromDate;
  }

  return {
    fromDate: currentFromDate,
    toDate: newToDate,
    changed: newToDate !== currentToDate,
  };
}

interface FormActionsParams {
  resolvedTripOptionId: string | null;
  currentTripOption: any;
  selectedPlanSegment: any;
  pricingViewId: string | null;
  fromDate: Date;
  toDate: Date;
  travellerCount: number;
  selectedCountriesCount: number;
  segments: Segment[];
  travellerMin?: number;
  travellerMax?: number;
  travCountError?: string;
  setSelectedTripOptionId: (id: string | null) => void;
  setSelectedTripOption: (opt: any) => void;
  setSelectedCountries: (c: Country[]) => void;
  setFromDate: (d: Date) => void;
  setToDate: (d: Date) => void;
  setTravellerCount: (n: number) => void;
  setPlanSegmentId: (id: string | null) => void;
  setPricingViewId: (id: string | null) => void;
  onCountryChange?: (countries: Country[]) => void;
  onDateChange?: (from: Date, to: Date) => void;
  onTravellerChange?: (count: number) => void;
  onSegmentChange?: (segments: Segment[]) => void;
  onTripOptionChange?: (id: string | null) => void;
  onCTAPress?: () => void;
  isV2Endpoint: boolean;
}

export function useTravelInsuranceFormActions({
  resolvedTripOptionId,
  currentTripOption,
  selectedPlanSegment,
  pricingViewId,
  fromDate,
  toDate,
  travellerCount,
  selectedCountriesCount,
  segments,
  setSelectedTripOptionId,
  setSelectedTripOption,
  setSelectedCountries,
  setFromDate,
  setToDate,
  setTravellerCount,
  setPlanSegmentId,
  setPricingViewId,
  onCountryChange,
  onDateChange,
  onTravellerChange,
  onSegmentChange,
  onTripOptionChange,
  onCTAPress,
  travellerMin = 1,
  travellerMax = 20,
  travCountError,
  isV2Endpoint
}: FormActionsParams) {
  const analytics = getAnalytics();

  const [formCountryErrorMsg, setFormCountryErrorMsg] = useState("");
  const [formDateErrorMsg, setFormDateErrorMsg] = useState("");
  const [formTravellerErrorMsg, setFormTravellerErrorMsg] = useState("");
  const [formValidationToastMsg, setFormValidationToastMsg] = useState("");
  const [exploreLoading, setExploreLoading] = useState(false);
  const [segmentModalHandled, setSegmentModalHandled] = useState(false);
  const [isSegmentModalShown, setIsSegmentModalShown] = useState(false);
  const [isTripOptionModalShown, setIsTripOptionModalShown] = useState(false);
  const [tripOptionModalHandled, setTripOptionModalHandled] = useState(false);

  const validationToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Stash planType + countries when switching TO multitrip so they can be
   *  restored on switch-back — mirrors DT's tempUserData pattern. */
  const savedUserDataRef = useRef<{ planType: string | null; countryCodes: string }>({
    planType: null,
    countryCodes: "",
  });
  const previousCalendarTypeRef = useRef<string | null>(null);

  useEffect(() => () => {
    if (validationToastTimeoutRef.current) clearTimeout(validationToastTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (previousCalendarTypeRef.current !== null) return;
    if (!currentTripOption) return;
    const isSingle =
      currentTripOption?.calenderSelectionType === "SINGLE" &&
      currentTripOption?.config?.durationAllowed;
    previousCalendarTypeRef.current = isSingle ? "SINGLE" : "NON_SINGLE";
  }, [currentTripOption]);

  useEffect(() => { if (selectedCountriesCount > 0) setFormCountryErrorMsg(""); }, [selectedCountriesCount]);
  useEffect(() => { if (fromDate && toDate) setFormDateErrorMsg(""); }, [fromDate, toDate]);
  useEffect(() => {
    if (travellerCount >= travellerMin && travellerCount <= travellerMax) {
      setFormTravellerErrorMsg("");
    }
  }, [travellerCount]);

  useEffect(() => { setTripOptionModalHandled(false); setIsTripOptionModalShown(false); }, [resolvedTripOptionId]);
  useEffect(() => { setSegmentModalHandled(false); setIsSegmentModalShown(false); }, [selectedPlanSegment?.id]);

  const handleOptionSelect = useCallback((option: any) => {
    const isGoingToMultitrip =
      option.id === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID;
    const isLeavingMultitrip =
      resolvedTripOptionId === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID &&
      !isGoingToMultitrip;

    const sp =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;

    // ── 1. Save planType + countries before switching TO multitrip ──
    if (isGoingToMultitrip) {
      savedUserDataRef.current = {
        planType:
          sp?.get("planType") ||
          sp?.get("segment") ||
          selectedPlanSegment?.id ||
          null,
        countryCodes: sp?.get("countries") || "",
      };
    }

    setSelectedTripOptionId(option.id);
    setSelectedTripOption(option);
    setPlanSegmentId(null);
    setPricingViewId(null);
    onTripOptionChange?.(option.id);
    if (typeof window === "undefined") return;
    replaceHistory("tripOption", option.id);

    // ── 2. Restore saved planType when leaving multitrip ──
    const segList = option?.planTypes?.value as Segment[] | undefined;
    const savedPlanType = savedUserDataRef.current.planType;
    const targetSeg =
      isLeavingMultitrip && savedPlanType
        ? segList?.find((s) => s.id === savedPlanType) ??
          segList?.find((s) => s.isSelected) ??
          segList?.[0]
        : segList?.find((s) => s.isSelected) ?? segList?.[0];

    if (targetSeg?.id) {
      setPlanSegmentId(targetSeg.id);
      replaceHistoryPlanTypeAndSegment(targetSeg.id);
      const next = (segList || []).map((item) => ({
        ...item,
        isSelected: item.id === targetSeg.id,
      }));
      onSegmentChange?.(next);
    } else {
      replaceHistoryRemovePlanTypeAndSegment();
    }

    const pricingList = option?.pricingView?.value as PricingView[] | undefined;
    const firstPricing =
      pricingList?.find((p) => p.isSelected) ?? pricingList?.[0];
    if (firstPricing?.id) {
      setPricingViewId(firstPricing.id);
      replaceHistory("pricingView", firstPricing.id);
    } else {
      replaceHistoryRemoveParams("pricingView");
    }

    // ── 3. Handle countries on trip option switch ──
    const urlCountryCodes = (sp?.get("countries") || "")
      .split(",")
      .filter(Boolean);

    const countryResult = mapCountriesForTripOptionSwitch({
      isGoingToMultitrip,
      isLeavingMultitrip,
      newCountryListValue: option?.countryList?.value || [],
      urlCountryCodes,
      savedCountryCodes: savedUserDataRef.current.countryCodes,
    });

    if (countryResult) {
      setSelectedCountries(countryResult.countries);
      onCountryChange?.(countryResult.countries);
      if (countryResult.urlCode) {
        replaceHistory("countries", countryResult.urlCode);
      } else {
        replaceHistoryRemoveParams("countries");
      }
    }

    // ── 4. Recalculate dates based on new trip option's calendar config ──
    const dateResult = recalculateDatesForTripOption({
      option,
      targetSegConfig: targetSeg?.config,
      isGoingToMultitrip,
      isLeavingMultitrip,
      previousCalendarType: previousCalendarTypeRef.current,
      currentFromDate: fromDate,
      currentToDate: toDate,
    });

    const isSingleCalendar =
      option?.calenderSelectionType === "SINGLE" &&
      (isGoingToMultitrip ? option?.config : targetSeg?.config || option?.config)?.durationAllowed;

    previousCalendarTypeRef.current = isSingleCalendar
      ? "SINGLE"
      : "NON_SINGLE";

    if (dateResult.changed) {
      setFromDate(dateResult.fromDate);
      setToDate(dateResult.toDate);
      onDateChange?.(dateResult.fromDate, dateResult.toDate);
      replaceHistory("startDate", String(convertDateToUnix(dateResult.fromDate)));
      replaceHistory("endDate", String(convertDateToUnix(dateResult.toDate)));
    }
  }, [
    resolvedTripOptionId,
    selectedPlanSegment?.id,
    fromDate,
    toDate,
    setSelectedTripOptionId,
    setSelectedTripOption,
    setPlanSegmentId,
    setPricingViewId,
    setSelectedCountries,
    setFromDate,
    setToDate,
    onTripOptionChange,
    onSegmentChange,
    onCountryChange,
    onDateChange,
  ]);

  const handleCountryChange = useCallback((countries: Country[]) => {
    setSelectedCountries(countries);
    onCountryChange?.(countries);
    if (typeof window === "undefined") return;
    const codes = countries.map((c) => c.code).filter(Boolean).join(",");
    if (codes) replaceHistory("countries", codes);
    else replaceHistoryRemoveParams("countries");
  }, [setSelectedCountries, onCountryChange]);

  const handleDateChange = useCallback((start: Date, end: Date) => {
    setFromDate(start);
    setToDate(end);
    onDateChange?.(start, end);
    if (typeof window === "undefined") return;
    replaceHistory("startDate", String(convertDateToUnix(start)));
    replaceHistory("endDate", String(convertDateToUnix(end)));
  }, [setFromDate, setToDate, onDateChange]);

  const handleTravellerChange = useCallback((count: number) => {
    setTravellerCount(count);
    onTravellerChange?.(count);

    if (count >= travellerMin && count <= travellerMax) {
      setFormTravellerErrorMsg("");
    } else if (travCountError) {
      setFormTravellerErrorMsg(travCountError);
    }

    if (typeof window === "undefined") return;
    replaceHistory("travellers", String(count));
  }, [setTravellerCount, onTravellerChange, travellerMin, travellerMax, travCountError]);

  const handleSegmentChange = useCallback((segment: Segment) => {
    setPlanSegmentId(segment.id);
    const nextSegments = segments.map((item) => ({ ...item, isSelected: item.id === segment.id }));
    onSegmentChange?.(nextSegments);
    if (typeof window === "undefined") return;
    if (segment.id) replaceHistoryPlanTypeAndSegment(segment.id);
    else replaceHistoryRemovePlanTypeAndSegment();
  }, [segments, setPlanSegmentId, onSegmentChange]);

  const handlePricingViewChange = useCallback((pricing: Segment) => {
    setPricingViewId(pricing.id);
    if (typeof window === "undefined") return;
    if (pricing.id) replaceHistory("pricingView", pricing.id);
    else replaceHistoryRemoveParams("pricingView");
  }, [setPricingViewId]);

  const handleExplorePlansClick = useCallback(async () => {
    if (typeof window === "undefined") return;
    setFormCountryErrorMsg("");
    setFormDateErrorMsg("");
    setFormTravellerErrorMsg("");
    setFormValidationToastMsg("");

    const searchParams = new URLSearchParams(window.location.search);
    const countrySelectErrMsg =
      currentTripOption?.countryList?.countrySelectErrMsg ||
      currentTripOption?.countryList?.error_message ||
      "Please select a country";

    let countries = searchParams.get("countries");
    const tripOptionFromUrl = searchParams.get("tripOption") || resolvedTripOptionId || "";

    if (tripOptionFromUrl === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID && countries) {
      const countryCodesToCheck = countries.split(",").map((c) => c.trim());
      countries = (countryCodesToCheck.includes("USA") || countryCodesToCheck.includes("CAN") || countryCodesToCheck.includes("WW")) ? "WW" : "WWEUC";
    }

    const countryCodes = countries ? countries.split(",").map((c) => c.trim()).filter(Boolean) : [];

    if (!countryCodes.length || !countryCodes[0]) {
      setFormCountryErrorMsg(countrySelectErrMsg);
      analytics?.trackCommonLandingEvents?.({ actionType: "error_shown", payload: countrySelectErrMsg });
      errorTrackEvent({ error_details_list: [{ message: countrySelectErrMsg, source: "country_select", severity: "P3" }] });
    }

    const planTypeId = searchParams.get("planType") || searchParams.get("segment") || selectedPlanSegment?.id || "";

    if (planTypeId === "STUDENT" && countryCodes[0] === "IN") {
      const msg = "We do not have domestic student plans, please select another country to see student plans";
      setFormValidationToastMsg(msg);
      if (validationToastTimeoutRef.current) clearTimeout(validationToastTimeoutRef.current);
      validationToastTimeoutRef.current = setTimeout(() => { setFormValidationToastMsg(""); validationToastTimeoutRef.current = null; }, 3000);
      analytics?.trackCommonLandingEvents?.({ actionType: "error_shown", payload: msg });
      errorTrackEvent({ error_details_list: [{ message: msg, source: "student_plan", severity: "P3" }] });
      return;
    }

    replaceHistory("segment", planTypeId || "");

    const sd = searchParams.get("startDate");
    const ed = searchParams.get("endDate");
    const tv = searchParams.get("travellers");

    const rangeFrom = sd && !Number.isNaN(parseInt(sd, 10)) ? convertUnixToDate(parseInt(sd, 10)) : fromDate;
    const rangeTo = ed && !Number.isNaN(parseInt(ed, 10)) ? convertUnixToDate(parseInt(ed, 10)) : toDate;

    const hasValidDates = rangeFrom instanceof Date && !Number.isNaN(rangeFrom.getTime()) && rangeTo instanceof Date && !Number.isNaN(rangeTo.getTime());
    if (!hasValidDates) setFormDateErrorMsg("Please select start and end dates");

    const travellerCountNum = tv ? parseInt(tv, 10) : travellerCount;
    const hasValidTravellers = !Number.isNaN(travellerCountNum) && travellerCountNum >= travellerMin && travellerCountNum <= travellerMax;
    if (!hasValidTravellers) {
      setFormTravellerErrorMsg(
        travCountError || `Please select number of travellers between ${travellerMin} and ${travellerMax}`
      );
    }

    const canProceed = countryCodes.length > 0 && Boolean(countryCodes[0]) && hasValidDates && hasValidTravellers;

    if (canProceed) {
      if (tripOptionFromUrl && currentTripOption?.modal && !tripOptionModalHandled) {
        setIsTripOptionModalShown(true);
        setTripOptionModalHandled(true);
        return;
      }
      if (selectedPlanSegment?.modal && !segmentModalHandled) {
        setIsSegmentModalShown(true);
        setSegmentModalHandled(true);
        return;
      }

      onCTAPress?.();
      analytics?.trackCommonLandingEvents({ actionType: "explore_plan_click" });
      trackEvent({ event_detail: { event_type: "action", event_name: "component_cta-clicked", event_value: "explore_plan_click" } });
      triggerPageExit();

      const selectedSegmentIdV2 = tripOptionFromUrl === COMMON_LANDING_CONSTANTS.MULTITRIP_OPTION_ID ? "MULTITRIP" : planTypeId;

      const requestData = {
        query: { itineraryId: searchParams.get("itineraryId") || "", source: "standalone_regular" },
        partner: getPartnerOnClientSide(),
        isV2Endpoint,
        userSearchIntent: {
          country: countryCodes,
          departureDateTime: convertDateToUnix(rangeFrom || getIndiaTodayDate()),
          arrivalDateTime: convertDateToUnix(rangeTo || rangeFrom || getIndiaTodayDate()),
          numberOfTravellers: travellerCountNum,
          ...(selectedSegmentIdV2 && { segment: selectedSegmentIdV2 }),
          ...(planTypeId && { planType: planTypeId }),
          ...(pricingViewId && { pricingView: pricingViewId }),
          tripOption: tripOptionFromUrl,
        },
      };

      setExploreLoading(true);
      try {
        storePageTransitionStart("landing");
        await navigateRecentSearchNextPage(requestData);
      } finally {
        setExploreLoading(false);
      }
    }

    analytics?.trackCommonLandingEvents({ actionType: "explore_clicked" });
  }, [
    analytics, currentTripOption, selectedPlanSegment, resolvedTripOptionId,
    fromDate, toDate, travellerCount, pricingViewId,
    tripOptionModalHandled, segmentModalHandled, onCTAPress,
  ]);

  return {
    formCountryErrorMsg,
    formDateErrorMsg,
    formTravellerErrorMsg,
    formValidationToastMsg,
    exploreLoading,
    isSegmentModalShown,
    setIsSegmentModalShown,
    setSegmentModalHandled,
    isTripOptionModalShown,
    setIsTripOptionModalShown,
    setTripOptionModalHandled,
    handleOptionSelect,
    handleCountryChange,
    handleDateChange,
    handleTravellerChange,
    handleSegmentChange,
    handlePricingViewChange,
    handleExplorePlansClick,
  };
}
